import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { AppText } from '../AppText';
import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import { Site } from '../../types/Site';
import {
  deviceTimeZone,
  formatAddressSummary,
  isValidTimeZone,
  timeZoneAbbreviation,
} from './siteFormatting';
import { computeSiteStatus } from './siteStatus';
import { useNow } from './useNow';
import { useTranslation } from '../../providers/TranslationProvider';
import { SiteAvatar } from './SiteAvatar';
import { SiteStatusPill } from './SiteStatusPill';
import { UnconfirmedHoursCard } from './UnconfirmedHoursCard';
import { EventBlock, eventHasVisibleContent } from './EventBlock';

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.grayBorder,
    borderRadius: 12,
    gap: 12,
  },
  padded: { padding: 16 },
  paddedCompact: { padding: 14 },
  openAccent: {
    borderLeftWidth: 4,
    borderLeftColor: Colors.darkGreen,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  headerText: {
    flex: 1,
    gap: 3,
  },
  name: {
    fontSize: 19,
    fontWeight: '700',
    lineHeight: 23,
    color: Colors.veryDarkGray,
  },
  address: {
    fontSize: 14,
    color: Colors.mediumGray,
  },
  chevron: {
    fontSize: 20,
    color: Colors.lightGray,
    alignSelf: 'center',
  },
  timezoneNote: {
    fontSize: 12,
    color: Colors.mediumGray,
  },
});

export interface SiteListItemProps {
  site: Site;
  onSelect: () => void;
}

export function SiteListItem({ site, onSelect }: SiteListItemProps) {
  const { t } = useTranslation();
  const now = useNow();
  const status = computeSiteStatus(site, now);
  const isCompact = Layout.isCompactScreen;
  // events is required per the Site type, but this is still-stabilizing API data -- fall back to
  // an empty list rather than throwing if a real response omits it.
  const events = site.events ?? [];
  // Only worth calling out when it's a well-formed zone that actually differs from the
  // neighbor's own device zone -- otherwise the note is just noise for the common case of
  // browsing nearby pantries (or, for a malformed value, not worth surfacing at all).
  const { timezone } = site;
  const showTimezoneNote =
    timezone !== undefined && isValidTimeZone(timezone) && timezone !== deviceTimeZone();

  return (
    <TouchableOpacity
      style={[
        styles.container,
        isCompact ? styles.paddedCompact : styles.padded,
        status.kind === 'open' && styles.openAccent,
      ]}
      onPress={onSelect}
      testID={`site-list-item-${site.siteId}`}
    >
      <View style={styles.headerRow}>
        <SiteAvatar name={site.name} neutral={status.kind === 'unconfirmed'} />
        <View style={styles.headerText}>
          <AppText style={styles.name}>{site.name}</AppText>
          {/* Distance is omitted here -- site.distanceMeters is measured from the map's search
              center, not the neighbor's actual location, so displaying it as "X mi" would show a
              distance that has nothing to do with how far the neighbor is from this site.
              SiteDetailsView/SitePreviewCard currently display this same search-center-relative
              value rather than a real neighbor-relative distance; see PR #811 (Details) and
              https://github.com/PlentifulApp/plentiful/issues/4042 (Preview). */}
          <AppText style={styles.address}>{formatAddressSummary(site)}</AppText>
        </View>
        {!isCompact && <AppText style={styles.chevron}>›</AppText>}
      </View>

      {status.kind === 'unconfirmed' ? (
        <UnconfirmedHoursCard phone={site.phone} />
      ) : (
        <>
          <SiteStatusPill status={status} now={now} timezone={timezone} />
          {/* EventBlock renders nothing for an event with no current/future windows and no
              hasMoreWindows -- filter to only the events that will actually render before
              computing showDivider, so a divider doesn't float above a card's first visible
              event when an earlier event was skipped. */}
          {events
            .filter((event) => eventHasVisibleContent(event, now))
            .map((event, index) => (
              <EventBlock
                key={event.eventId}
                event={event}
                now={now}
                showDivider={index > 0}
                timezone={timezone}
              />
            ))}
          {showTimezoneNote && timezone && (
            <AppText style={styles.timezoneNote}>
              {t('site_list_timezone_note', { zone: timeZoneAbbreviation(timezone, now) })}
            </AppText>
          )}
        </>
      )}
    </TouchableOpacity>
  );
}
